import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiFetch } from '../../utils/api';
import SessionManager from '../../components/SessionManager';
import ConfirmOffboardModal from '../../components/ConfirmOffboardModal';

/**
 * Account tab — shown only to customer users.
 * Sections:
 *   1. Account summary
 *   2. Active sessions (re-uses SessionManager)
 *   3. Deactivate account (amber, soft-delete with 30-day recovery)
 *   4. Delete account (red, behind disclosure, requires re-auth)
 */
export default function AccountTab() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'deactivate' | 'delete' | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async ({ reason, password }) => {
    setBusy(true);
    setError('');
    try {
      if (modalMode === 'deactivate') {
        const res = await apiFetch('/api/v1/profile/account/deactivate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason || null }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || 'request_failed');
        }
        setModalMode(null);
        // Force logout — sessions are invalidated server-side
        if (logout) await logout();
        navigate('/login?reason=deactivated', { replace: true });
        return;
      }
      if (modalMode === 'delete') {
        const res = await apiFetch('/api/v1/profile/account/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password, reason: reason || null }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || 'request_failed');
        }
        setModalMode(null);
        if (logout) await logout();
        navigate('/login?reason=deleted', { replace: true });
        return;
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Account summary */}
      <section className="rounded-lg p-6"
        style={{ background: 'var(--surface-secondary)', border: '1px solid var(--border-color)' }}>
        <h2 className="font-headline text-lg font-bold mb-4">
          {isKo ? '계정 정보' : 'Account info'}
        </h2>
        <dl className="text-sm space-y-2">
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>{isKo ? '이름' : 'Name'}</dt>
            <dd>{user?.full_name || '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>{isKo ? '이메일' : 'Email'}</dt>
            <dd>{user?.email || '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--text-secondary)' }}>{isKo ? '역할' : 'Role'}</dt>
            <dd>{user?.role || '—'}</dd>
          </div>
        </dl>
      </section>

      {/* Sessions */}
      <section>
        <h2 className="font-headline text-lg font-bold mb-3">
          {isKo ? '활성 세션' : 'Active sessions'}
        </h2>
        <SessionManager />
      </section>

      {/* Deactivate */}
      <section className="rounded-lg p-6"
        style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
        <h2 className="font-headline text-lg font-bold mb-2" style={{ color: '#92400E' }}>
          {isKo ? '계정 비활성화' : 'Deactivate account'}
        </h2>
        <p className="text-sm mb-4" style={{ color: '#78350F' }}>
          {isKo
            ? '계정을 비활성화하면 즉시 로그아웃되며, 30일 이내에 다시 로그인하면 계정이 복구됩니다. 30일이 지나면 영구 삭제됩니다.'
            : 'Deactivating signs you out immediately. You can restore the account by signing back in within 30 days; after that, it is permanently deleted.'}
        </p>
        <button
          onClick={() => { setError(''); setModalMode('deactivate'); }}
          className="px-4 py-2 rounded-md text-sm font-medium"
          style={{ background: '#F59E0B', color: '#fff' }}
          disabled={busy}
        >
          {isKo ? '계정 비활성화' : 'Deactivate account'}
        </button>
      </section>

      {/* Delete (disclosure) */}
      <section className="rounded-lg p-6"
        style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
        <h2 className="font-headline text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
          {isKo ? '계정 영구 삭제' : 'Delete account permanently'}
        </h2>
        <p className="text-sm mb-3" style={{ color: '#7F1D1D' }}>
          {isKo
            ? '이 작업은 되돌릴 수 없습니다. 모든 개인정보가 즉시 비식별화되며 복구할 수 없습니다.'
            : 'This action cannot be undone. All personal data is anonymized immediately and cannot be recovered.'}
        </p>
        {!showDeleteSection ? (
          <button
            onClick={() => setShowDeleteSection(true)}
            className="text-sm underline"
            style={{ color: '#991B1B' }}
          >
            {isKo ? '영구 삭제 옵션 표시' : 'Show permanent deletion option'}
          </button>
        ) : (
          <button
            onClick={() => { setError(''); setModalMode('delete'); }}
            className="px-4 py-2 rounded-md text-sm font-medium"
            style={{ background: '#DC2626', color: '#fff' }}
            disabled={busy}
          >
            {isKo ? '계정 영구 삭제' : 'Permanently delete my account'}
          </button>
        )}
      </section>

      {error && (
        <div className="rounded-md p-3 text-sm"
          style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5' }}>
          {error}
        </div>
      )}

      <ConfirmOffboardModal
        open={!!modalMode}
        mode={modalMode || 'deactivate'}
        userEmail={user?.email}
        busy={busy}
        onCancel={() => { if (!busy) setModalMode(null); }}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
